import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  type IssueResponseDto,
  type SaveIssueRequestDto,
  useApi,
} from '@issuemanager/api-client';
import { type ExtendedAxiosError } from '@issuemanager/apps/client/hooks';

type Params = {
  code: string;
  onSuccess?: (response: IssueResponseDto) => void;
  onError?: (error: ExtendedAxiosError) => void;
};

export function useSaveIssue({ code, onSuccess, onError }: Params) {
  const api = useApi();

  const queryClient = useQueryClient();

  return useMutation<IssueResponseDto, ExtendedAxiosError, SaveIssueRequestDto>(
    {
      mutationKey: ['issues', code],
      mutationFn: (r) => api.issuesApi.saveIssue(code, r).then((r) => r.data),
      onSuccess: async (response) => {
        queryClient.setQueryData<IssueResponseDto>(['issues', code], response);
        await Promise.all([
          queryClient.invalidateQueries({
            queryKey: ['issues', code],
          }),
          queryClient.invalidateQueries({
            queryKey: ['users', 'roles'],
          }),
          queryClient.invalidateQueries({
            queryKey: ['user-roles', code],
          }),
        ]);
        onSuccess?.(response);
      },
      onError,
    },
  );
}
